import { supabase } from '../lib/supabase';

export const verificationService = {
  /**
   * Generate and send OTP to user's email
   * @param {string} email - User's email address
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async sendVerificationCode(email) {
    try {
      // Generate 6-digit OTP
      const otpCode = Math.floor(100000 + Math.random() * 900000)?.toString();
      
      // Calculate expiration time (10 minutes from now)
      const expiresAt = new Date();
      expiresAt?.setMinutes(expiresAt?.getMinutes() + 10);

      // Store OTP in database
      const { error: dbError } = await supabase?.from('email_verification_otps')?.insert({
          user_email: email,
          otp_code: otpCode,
          expires_at: expiresAt?.toISOString(),
          verified: false
        });

      if (dbError) throw dbError;

      // Send OTP via edge function
      const { data, error: emailError } = await supabase?.functions?.invoke(
        'send-verification-otp',
        {
          body: { email, otpCode }
        }
      );

      if (emailError) throw emailError;
      if (!data?.success) throw new Error(data?.error || 'Failed to send verification code');

      return {
        success: true,
        message: 'Verification code sent to your email'
      };
    } catch (error) {
      console.error('Send verification code error:', error);
      throw new Error(error?.message || 'Failed to send verification code');
    }
  },

  /**
   * Verify OTP code
   * @param {string} email - User's email address
   * @param {string} otpCode - OTP code to verify
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async verifyCode(email, otpCode) {
    try {
      // Find valid OTP
      const { data: otpRecords, error: fetchError } = await supabase?.from('email_verification_otps')?.select('*')?.eq('user_email', email)?.eq('otp_code', otpCode)?.eq('verified', false)?.gt('expires_at', new Date()?.toISOString())?.order('created_at', { ascending: false })?.limit(1);

      if (fetchError) throw fetchError;

      if (!otpRecords || otpRecords?.length === 0) {
        return {
          success: false,
          message: 'Invalid or expired verification code'
        };
      }

      // Mark OTP as verified
      const { error: updateError } = await supabase?.from('email_verification_otps')?.update({
          verified: true,
          verified_at: new Date()?.toISOString()
        })?.eq('id', otpRecords?.[0]?.id);

      if (updateError) throw updateError;

      return {
        success: true,
        message: 'Verification successful'
      };
    } catch (error) {
      console.error('Verify code error:', error);
      throw new Error(error?.message || 'Failed to verify code');
    }
  },

  /**
   * Clean up expired OTPs for a user
   * @param {string} email - User's email address
   */
  async cleanupExpiredOTPs(email) {
    try {
      const { error } = await supabase?.from('email_verification_otps')?.delete()?.eq('user_email', email)?.lt('expires_at', new Date()?.toISOString())?.eq('verified', false);

      if (error) throw error;
    } catch (error) {
      console.error('Cleanup expired OTPs error:', error);
      // Non-critical error, don't throw
    }
  }
};